<template>
  <div class="crm-page">
    <div class="crm-hero">
      <div>
        <div class="crm-eyebrow">客户管理CRM</div>
        <h2>销售工作台</h2>
        <p>
          面向员工高频使用，聚合今日待拨客户、快捷结果记录、下次回访设置、进度看板和排行榜。
        </p>
      </div>
      <div class="crm-actions">
        <el-button type="primary" icon="el-icon-video-play" @click="beginWork"
          >一键开始工作</el-button
        >
        <el-button
          icon="el-icon-phone-outline"
          @click="notifyPending('快速拨号')"
          >快速拨号（待开发）</el-button
        >
      </div>
    </div>

    <div class="work-metrics">
      <div class="metric-card">
        <span>今日待拨客户</span><strong>{{ todayTasks.length }}</strong>
      </div>
      <div class="metric-card success">
        <span>今日已完成跟进</span><strong>{{ todayCompleted }}</strong>
      </div>
      <div class="metric-card warning">
        <span>高意向待推进</span><strong>{{ highIntentCount }}</strong>
      </div>
      <div class="metric-card primary">
        <span>今日目标完成度</span><strong>{{ todayGoalRate }}%</strong>
      </div>
    </div>

    <el-row :gutter="16">
      <el-col :span="10">
        <el-card shadow="never">
          <div slot="header" class="card-header">
            <span>今日待拨客户</span>
            <el-tag size="mini" type="warning">点击左侧客户即可开始记录</el-tag>
          </div>
          <div class="task-list">
            <div
              v-for="item in todayTasks"
              :key="item.id"
              class="task-item"
              :class="{
                active: activeCustomer && activeCustomer.id === item.id,
              }"
              @click="selectCustomer(item)"
            >
              <div>
                <div class="task-name">
                  {{ item.name }} · {{ item.companyName || "未填写公司" }}
                </div>
                <div class="task-meta">
                  <crm-phone-field
                    :phone="item.phone"
                    :status="taskPhoneStatus(item)"
                  />
                  <span>· {{ item.nextFollowTime || "待首次联系" }}</span>
                </div>
              </div>
              <el-tag size="mini" :type="tagType(item)">{{
                item.intentionLevel
              }}</el-tag>
            </div>
          </div>
        </el-card>

        <el-card shadow="never" style="margin-top: 16px">
          <div slot="header">今日排行榜</div>
          <div class="rank-list">
            <div
              v-for="(item, index) in rankList"
              :key="item.name"
              class="rank-item"
            >
              <span>{{ index + 1 }}. {{ item.name }}</span>
              <b>{{ item.value }}</b>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :span="14">
        <el-card shadow="never">
          <div slot="header" class="card-header">
            <span>快捷跟进面板</span>
            <el-tag size="mini" type="info"
              >当前选中：{{
                activeCustomer ? activeCustomer.name : "未选择客户"
              }}</el-tag
            >
          </div>
          <template v-if="activeCustomer">
            <div class="workbench-tools">
              <div class="tool-card">
                <div class="tool-card-title">外呼入口预留</div>
                <div class="tool-card-actions">
                  <crm-phone-field
                    :phone="activeCustomer.phone"
                    :status="taskPhoneStatus(activeCustomer)"
                    prefix="主号码"
                  />
                  <el-button size="mini" @click="notifyPending('微信联动')"
                    >微信按钮（待开发）</el-button
                  >
                  <el-button size="mini" @click="notifyPending('快速记录')"
                    >快速记录（待开发）</el-button
                  >
                </div>
                <div class="tool-card-note">
                  后续将支持网页点击电话按钮，向已登录手机 App
                  发送外呼请求，手机端自动唤起拨号并回传通话结果。
                </div>
              </div>
            </div>
            <div class="quick-tags">
              <el-button
                v-for="item in quickButtons"
                :key="item.key"
                :type="quickAction === item.key ? 'primary' : 'default'"
                size="mini"
                @click="quickAction = item.key"
              >
                {{ item.label }}
              </el-button>
            </div>
            <el-row :gutter="12" style="margin-top: 12px">
              <el-col :span="8">
                <el-select
                  v-model="quickMethod"
                  placeholder="跟进方式"
                  style="width: 100%"
                >
                  <el-option label="电话" value="电话"></el-option>
                  <el-option label="微信" value="微信"></el-option>
                  <el-option label="面谈" value="面谈"></el-option>
                </el-select>
              </el-col>
              <el-col :span="8">
                <el-date-picker
                  v-model="nextFollowTime"
                  type="datetime"
                  value-format="yyyy-MM-dd HH:mm"
                  placeholder="下次回访时间"
                  style="width: 100%"
                />
              </el-col>
              <el-col :span="8">
                <el-select
                  v-model="quickIntention"
                  placeholder="意向等级"
                  style="width: 100%"
                >
                  <el-option label="A高意向" value="A高意向"></el-option>
                  <el-option label="B中意向" value="B中意向"></el-option>
                  <el-option label="C低意向" value="C低意向"></el-option>
                  <el-option label="D无意向" value="D无意向"></el-option>
                </el-select>
              </el-col>
            </el-row>
            <el-input
              type="textarea"
              :rows="4"
              v-model="quickSummary"
              placeholder="快速记录本次沟通摘要、客户反馈、报价进展、下一步动作"
              style="margin-top: 12px"
            />
            <div class="quick-actions">
              <el-button @click="fillSummary">生成演示摘要</el-button>
              <el-button type="primary" @click="submitQuickFollow"
                >保存本次结果</el-button
              >
            </div>
          </template>
          <el-empty v-else description="请选择一位客户开始今日工作"></el-empty>
        </el-card>

        <el-card shadow="never" style="margin-top: 16px">
          <div slot="header">快速拨号记录区（前端模拟）</div>
          <div class="call-log">
            <div
              v-for="item in todayRecords"
              :key="item.id"
              class="call-log-item"
            >
              <div>
                <div class="call-log-title">
                  {{ item.customerName }} · {{ item.method }} ·
                  {{ item.result }}
                </div>
                <div class="call-log-meta">
                  {{ item.followTime }} · {{ item.summary }}
                </div>
              </div>
              <el-tag size="mini" :type="item.valid ? 'success' : 'info'">{{
                item.valid ? "有效跟进" : "普通记录"
              }}</el-tag>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { createOperationLog, loadCrmState, saveCrmState } from "./mock";
import CrmPhoneField from "./PhoneField.vue";

function formatNow() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function todayString() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )}`;
}

export default {
  name: "CrmWorkbench",
  components: { CrmPhoneField },
  data() {
    return {
      state: loadCrmState(this.$store.getters.GET_TITLE || "当前员工"),
      activeCustomer: null,
      quickAction: "connected",
      quickMethod: "电话",
      nextFollowTime: "",
      quickIntention: "B中意向",
      quickSummary: "",
      quickButtons: [
        { key: "missed", label: "未接通" },
        { key: "connected", label: "已接通" },
        { key: "wechat", label: "已加微信" },
        { key: "later", label: "稍后再打" },
        { key: "high", label: "高意向" },
        { key: "appointment", label: "已预约" },
        { key: "quote", label: "已报价" },
        { key: "public", label: "转公海" },
        { key: "lost", label: "转流失" },
      ],
    };
  },
  computed: {
    ownerName() {
      return this.$store.getters.GET_TITLE || "当前员工";
    },
    myCustomers() {
      return (this.state.customers || []).filter(
        (item) =>
          item.ownerName === this.ownerName && item.poolType === "私人池"
      );
    },
    todayTasks() {
      const today = todayString();
      return this.myCustomers.filter(
        (item) =>
          !item.firstCallDone ||
          String(item.nextFollowTime || "").startsWith(today) ||
          item.publicPoolCountdown <= 1
      );
    },
    todayRecords() {
      return (this.state.followRecords || []).filter(
        (item) =>
          item.employeeName === this.ownerName &&
          String(item.followTime || "").startsWith(todayString())
      );
    },
    todayCompleted() {
      return this.todayRecords.length;
    },
    highIntentCount() {
      return this.myCustomers.filter(
        (item) => item.intentionLevel === "A高意向"
      ).length;
    },
    todayGoalRate() {
      const goal = 12;
      return Math.min(100, Math.round((this.todayCompleted / goal) * 100));
    },
    rankList() {
      return this.state.analysisSeries.employeeCallRank || [];
    },
  },
  methods: {
    taskPhoneStatus(item) {
      if (!item) return "pending";
      if (item.mainStatus === "已流失") return "failed";
      if (item.publicPoolCountdown <= 0) return "offline";
      if (item.intentionLevel === "A高意向") return "ready";
      if (
        item.nextFollowTime &&
        String(item.nextFollowTime).startsWith(todayString())
      )
        return "dialing";
      return "pending";
    },
    notifyPending(label) {
      this.$message.warning(`${label}为预留功能，本阶段仅做工作台操作演示。`);
    },
    tagType(item) {
      const map = { A高意向: "danger", B中意向: "warning", C低意向: "info" };
      return map[item.intentionLevel] || "info";
    },
    persistState() {
      saveCrmState(this.state);
      this.state = loadCrmState(this.ownerName);
    },
    beginWork() {
      if (!this.todayTasks.length) {
        this.$message.success("今日暂无待拨客户，当前仅演示工作台空闲状态。");
        return;
      }
      this.selectCustomer(this.todayTasks[0]);
    },
    selectCustomer(customer) {
      this.activeCustomer = customer;
      this.nextFollowTime = customer.nextFollowTime || "";
      this.quickIntention = customer.intentionLevel || "B中意向";
      this.quickSummary = "";
      this.quickAction = "connected";
    },
    fillSummary() {
      if (!this.activeCustomer) return;
      const textMap = {
        missed: "本次电话未接通，建议稍后重新拨打。",
        connected: "客户已接通，已确认当前需求和预算。",
        wechat: "客户已加微信，已发送公司介绍与案例资料。",
        later: "客户表示稍后再联系，建议安排下一次提醒。",
        high: "客户意向较高，已进入重点推进名单。",
        appointment: "客户已预约面谈/到店，需提前准备方案。",
        quote: "已发送报价，等待客户反馈。",
        public: "客户长期未响应，拟转入公海池。",
        lost: "客户当前无继续推进意愿，拟转流失客户。",
      };
      this.quickSummary = `${this.activeCustomer.name}：${
        textMap[this.quickAction] || "已记录本次工作结果。"
      }`;
    },
    submitQuickFollow() {
      if (!this.activeCustomer) {
        this.$message.warning("请先选择一个客户");
        return;
      }
      if (!this.quickSummary) {
        this.$message.warning("请先填写沟通摘要");
        return;
      }
      const target = (this.state.customers || []).find(
        (item) => item.id === this.activeCustomer.id
      );
      if (!target) return;
      const time = formatNow();
      target.firstCallDone = true;
      target.lastFollowTime = time;
      target.nextFollowTime = this.nextFollowTime;
      target.intentionLevel = this.quickIntention;

      const resultMap = {
        missed: {
          main: "待首次联系",
          sub: "未接通",
          result: "未接通",
          valid: false,
        },
        connected: {
          main: "跟进中",
          sub: "已接通",
          result: "已接通",
          valid: true,
        },
        wechat: {
          main: "已加微信",
          sub: "有意向",
          result: "已加微信",
          valid: true,
          hasWeChat: true,
        },
        later: {
          main: "跟进中",
          sub: "稍后再打",
          result: "稍后再打",
          valid: true,
        },
        high: {
          main: "跟进中",
          sub: "有意向",
          result: "有效沟通",
          valid: true,
          level: "A高意向",
        },
        appointment: {
          main: "已预约",
          sub: "已面谈",
          result: "已预约",
          valid: true,
          level: "A高意向",
        },
        quote: {
          main: "已报价",
          sub: "已发送报价",
          result: "已报价",
          valid: true,
          hasWeChat: true,
        },
        public: {
          main: "公海池",
          sub: "等回复",
          result: "转公海",
          valid: false,
          public: true,
        },
        lost: {
          main: "已流失",
          sub: "无意向",
          result: "转流失",
          valid: false,
          lost: true,
        },
      };

      const config = resultMap[this.quickAction] || resultMap.connected;
      target.mainStatus = config.main;
      target.subStatus = config.sub;
      target.firstCallResult = config.result;
      if (config.level) target.intentionLevel = config.level;
      if (config.hasWeChat) target.hasWeChat = true;
      if (config.public) {
        target.poolType = "公海池";
        target.ownerName = "";
        target.publicReason = "工作台快捷转公海";
        target.publicAt = time;
      }
      if (config.lost) {
        target.lostReason = "暂不考虑";
      }

      target.timeline = target.timeline || [];
      target.timeline.unshift({
        time,
        type: this.quickMethod,
        title: config.result,
        desc: this.quickSummary,
        operator: this.ownerName,
      });

      this.state.followRecords.unshift({
        id: `FR${Date.now()}`,
        customerId: target.id,
        customerName: target.name,
        employeeName: this.ownerName,
        followTime: time,
        method: this.quickMethod,
        result: config.result,
        summary: this.quickSummary,
        valid: config.valid,
        hasWeChat: target.hasWeChat,
        nextFollowTime: this.nextFollowTime,
        qcStatus: "待质检",
        hasAttachment: false,
      });
      this.state.operationLogs.unshift(
        createOperationLog(
          this.ownerName,
          "销售工作台",
          "快捷跟进",
          target.name,
          `${this.quickMethod}记录`,
          "成功",
          config.result
        )
      );
      this.persistState();
      this.activeCustomer =
        (this.state.customers || []).find((item) => item.id === target.id) ||
        null;
      this.$message.success("本次工作结果已记录到 CRM（前端 mock 演示）");
    },
  },
};
</script>

<style scoped>
.crm-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.crm-hero {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 24px 28px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e8edf5;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.04);
}
.crm-eyebrow {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(64, 158, 255, 0.12);
  color: #409eff;
  font-size: 12px;
  font-weight: 700;
}
.crm-hero h2 {
  margin: 12px 0 0;
  font-size: 28px;
  color: #111827;
}
.crm-hero p {
  margin: 10px 0 0;
  color: #6b7280;
  line-height: 1.7;
}
.crm-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.work-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}
.metric-card {
  padding: 18px 20px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8edf5;
}
.metric-card span {
  color: #6b7280;
}
.metric-card strong {
  display: block;
  margin-top: 8px;
  font-size: 32px;
  color: #111827;
}
.metric-card.success {
  background: #f0fdf4;
}
.metric-card.warning {
  background: #fffaf0;
}
.metric-card.primary {
  background: #eff6ff;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.task-list,
.rank-list,
.call-log {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.task-item,
.call-log-item,
.rank-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fbff;
  border: 1px solid #edf2f7;
  cursor: pointer;
}
.task-item.active {
  border-color: #409eff;
  background: #eff6ff;
}
.task-name,
.call-log-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.task-meta,
.call-log-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.7;
}
.task-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.workbench-tools {
  margin-bottom: 14px;
}
.tool-card {
  padding: 14px 16px;
  border-radius: 14px;
  background: #f8fbff;
  border: 1px solid #dbeafe;
}
.tool-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.tool-card-actions {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.tool-card-note {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.7;
  color: #6b7280;
}
.quick-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.quick-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}
@media (max-width: 1200px) {
  .crm-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .work-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
